import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert, { AlertColor } from "@mui/material/Alert";
import Fade from "@mui/material/Fade";

type SnackbarState = {
  open: boolean;
  severity?: AlertColor;
  message?: string;
  duration?: number;
};

const DEFAULT_AUTO_HIDE_DURATION = 6000; // 6s

/**
 * Context type with all functions and states needed
 */
type SnackbarContextType = {
  snackbarState: SnackbarState;
  showSnackbar: (
    message: string,
    severity?: AlertColor,
    duration?: number
  ) => void;
  hideSnackbar: () => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
};

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined
);

/**
 * Snackbar Provider Component
 *
 * Provides a global snackbar for displaying notifications in the app.
 * Wraps the application to provide snackbar functionality.
 *
 * @example
 * ```tsx
 * // In your App.tsx or root component
 * import { SnackbarProvider } from './Snackbar';
 *
 * function App() {
 *   return (
 *     <SnackbarProvider>
 *       <YourApp />
 *     </SnackbarProvider>
 *   );
 * }
 * ```
 */
export function SnackbarProvider({ children }: { children: ReactNode }) {
  const [snackbarState, setSnackbarState] = useState<SnackbarState>({
    open: false,
    severity: "info",
    message: "",
    duration: 6000,
  });

  /**
   * Show a snackbar with custom message, severity and duration
   */
  const showSnackbar = useCallback(
    (
      message: string,
      severity: AlertColor = "info",
      duration = DEFAULT_AUTO_HIDE_DURATION
    ) => {
      setSnackbarState({
        open: true,
        severity,
        message,
        duration,
      });
    },
    []
  );

  /**
   * Hide the currently displayed snackbar
   */
  const hideSnackbar = useCallback(() => {
    setSnackbarState((prev) => ({ ...prev, open: false }));
  }, []);

  /**
   * Helper functions for different severity levels
   */
  const showSuccess = useCallback(
    (message: string, duration?: number) => {
      showSnackbar(message, "success", duration);
    },
    [showSnackbar]
  );

  const showError = useCallback(
    (message: string, duration?: number) => {
      showSnackbar(message, "error", duration);
    },
    [showSnackbar]
  );

  const showWarning = useCallback(
    (message: string, duration?: number) => {
      showSnackbar(message, "warning", duration);
    },
    [showSnackbar]
  );

  const showInfo = useCallback(
    (message: string, duration?: number) => {
      showSnackbar(message, "info", duration);
    },
    [showSnackbar]
  );

  /**
   * Memoize context value to prevent unnecessary re-renders
   */
  const contextValue = useMemo(
    () => ({
      snackbarState,
      showSnackbar,
      hideSnackbar,
      showSuccess,
      showError,
      showWarning,
      showInfo,
    }),
    [
      snackbarState,
      showSnackbar,
      hideSnackbar,
      showSuccess,
      showError,
      showWarning,
      showInfo,
    ]
  );

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={snackbarState.open}
        autoHideDuration={snackbarState.duration}
        onClose={hideSnackbar}
        TransitionComponent={Fade}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        id="global-snackbar"
      >
        <Alert
          onClose={hideSnackbar}
          severity={snackbarState.severity}
          elevation={6}
          sx={{ width: "100%", minWidth: 400, maxWidth: 600 }}
          id={`global-snackbar-alert-${snackbarState.severity}`}
        >
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
}

/**
 * Custom hook to use the snackbar in any component
 *
 * @returns {SnackbarContextType} Object containing all snackbar functions
 *
 * @example
 * ```tsx
 * import { useSnackbar } from './Snackbar';
 *
 * function MyComponent() {
 *   const { showSuccess, showError } = useSnackbar();
 *
 *   const handleSave = async () => {
 *     try {
 *       await saveData();
 *       showSuccess('Data saved successfully!');
 *     } catch (error) {
 *       showError('Failed to save data');
 *     }
 *   };
 *
 *   return <button onClick={handleSave}>Save</button>;
 * }
 * ```
 *
 * @throws {Error} If used outside of a SnackbarProvider
 */
export function useSnackbar(): SnackbarContextType {
  const context = useContext(SnackbarContext);

  if (context === undefined) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }

  return context;
}
